import { useState, useEffect } from 'react';
import { Check, Lock, Infinity, Users, HardDrive, Mail, Send, Layers, ChevronDown, FileSpreadsheet, FileText, Boxes } from 'lucide-react';
import { Spinner } from '@/components/shared/UIComponents';
import { fetchActivePlans, fetchPlanFeatureDefaults } from '@/lib/api';
import { FEATURE_META, APP_FEATURE_KEYS, PLATFORM_FEATURE_KEYS } from '@/lib/constants';
import { cn } from '@/utils/helpers';
import type { TenantPlan } from '@/types';

// ── Types ───────────────────────────────────────────────────────────────────

interface PlanDefault {
  enabled: boolean;
  limit: number | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatStorage(mb: number): string {
  if (mb >= 1000) return `${(mb / 1000).toFixed(mb % 1000 === 0 ? 0 : 1)} GB`;
  return `${mb} MB`;
}

function formatCampaignLimit(value: number): string {
  if (value === -1) return 'Unlimited';
  if (value === 0) return 'None';
  return String(value);
}

function formatPrice(cents: number): string {
  if (cents === 0) return 'Free';
  const dollars = cents / 100;
  return `$${Number.isInteger(dollars) ? dollars : dollars.toFixed(2)}`;
}

function formatLimit(limit: number | null | undefined): string {
  if (limit === null || limit === undefined) return 'Unlimited';
  if (limit === -1) return 'Unlimited';
  return String(limit);
}

function formatB2CLimit(value: number): string {
  if (value === -1) return 'Unlimited';
  return String(value);
}

function groupByCategory(keys: string[]): [string, string[]][] {
  const g: Record<string, string[]> = {};
  for (const k of keys) {
    const cat = FEATURE_META[k]?.category;
    if (!cat) continue;
    if (!g[cat]) g[cat] = [];
    g[cat].push(k);
  }
  for (const cat of Object.keys(g)) {
    g[cat].sort((a, b) => (FEATURE_META[a]?.label || '').localeCompare(FEATURE_META[b]?.label || ''));
  }
  return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
}

// ── Component ────────────────────────────────────────────────────────────────

export function PlatformPlansPage({ currentPlanId }: { currentPlanId?: string }) {
  const [plans, setPlans] = useState<TenantPlan[]>([]);
  const [featureMap, setFeatureMap] = useState<Record<string, Record<string, PlanDefault>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-category collapse state. Tier-prefixed keys ("app:Core" /
  // "platform:Branding") so categories with the same name don't
  // collide between the two tiers. Default-collapsed for everything
  // except Core to avoid an overwhelming first-paint on tenants with
  // many features enabled.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  // Tracks which export is mid-flight so we can disable both buttons
  // and surface "Exporting…" in the button text. Declared up here
  // (with the other hooks) — NOT inside the body below the early
  // returns — because Rules of Hooks: hook count has to be stable
  // across renders, and the early-return branches above would
  // otherwise change the count between loading→ready transitions.
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);
  const toggleCat = (key: string) => setCollapsed(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const activePlans = await fetchActivePlans();
        if (cancelled) return;

        const allDefaults = await Promise.all(
          activePlans.map((p) => fetchPlanFeatureDefaults(p.id)),
        );
        if (cancelled) return;

        const map: Record<string, Record<string, PlanDefault>> = {};
        activePlans.forEach((p, i) => {
          const m: Record<string, PlanDefault> = {};
          for (const d of allDefaults[i]) {
            m[d.feature_key] = { enabled: d.is_enabled, limit: d.limit_value ?? null };
          }
          map[p.id] = m;
        });

        setPlans(activePlans);
        setFeatureMap(map);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-panel p-6 text-center">
        <p className="text-red-600 dark:text-red-400 text-sm">Failed to load plans: {error}</p>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="glass-panel p-6 text-center">
        <p className="text-surface-500 text-sm">No active plans found.</p>
      </div>
    );
  }

  const appCategories = groupByCategory(APP_FEATURE_KEYS);
  const platformCategories = groupByCategory(PLATFORM_FEATURE_KEYS);
  const colCount = plans.length + 1;

  // ── Per-plan enabled-feature counts ───────────────────────────────
  // Drives the three "Features Enabled" stat rows AND the Excel / PDF
  // exports. B2C = app-tier feature keys (the 24 end-user-facing
  // features), B2B = platform-tier keys (api_access, sso, content
  // feeds, bulletin board, custom branding/domain). Total = sum.
  //
  // FEATURE_META lives in the bundle so we always have a stable
  // denominator regardless of whether a tenant has rows for every
  // key in plan_feature_defaults yet.
  const featureTotals = {
    b2c:   APP_FEATURE_KEYS.length,
    b2b:   PLATFORM_FEATURE_KEYS.length,
    total: APP_FEATURE_KEYS.length + PLATFORM_FEATURE_KEYS.length,
  };

  const countEnabled = (planId: string, keys: string[]): number => {
    const planRow = featureMap[planId];
    if (!planRow) return 0;
    let n = 0;
    for (const k of keys) {
      if (planRow[k]?.enabled === true) n++;
    }
    return n;
  };

  const STAT_ROWS: { label: string; icon: React.ElementType; getValue: (p: TenantPlan) => string }[] = [
    { label: 'Max Users', icon: Users, getValue: (p) => p.max_users === null ? 'Unlimited' : String(p.max_users) },
    { label: 'Storage', icon: HardDrive, getValue: (p) => formatStorage(p.max_storage_mb) },
    { label: 'Campaigns/Mo', icon: Mail, getValue: (p) => formatCampaignLimit(p.max_campaigns_per_month) },
    { label: 'Max Recipients', icon: Send, getValue: (p) => p.max_campaigns_per_month === 0 ? 'N/A' : String(p.max_campaign_recipients) },
    { label: 'B2C Plans', icon: Layers, getValue: (p) => formatB2CLimit(p.max_b2c_plans) },
    // Three new totals rows — sit at the bottom of the Plan Stats
    // section so the user sees the big-picture coverage before the
    // per-feature drill-down. Format "X of Y" matches the convention
    // used elsewhere in the admin UI (e.g. usage counters).
    {
      label:    'B2C Features',
      icon:     Boxes,
      getValue: (p) => `${countEnabled(p.id, APP_FEATURE_KEYS)} of ${featureTotals.b2c}`,
    },
    {
      label:    'B2B Features',
      icon:     Boxes,
      getValue: (p) => `${countEnabled(p.id, PLATFORM_FEATURE_KEYS)} of ${featureTotals.b2b}`,
    },
    {
      label:    'Total Features',
      icon:     Boxes,
      getValue: (p) => `${
        countEnabled(p.id, APP_FEATURE_KEYS) + countEnabled(p.id, PLATFORM_FEATURE_KEYS)
      } of ${featureTotals.total}`,
    },
  ];

  const isCurrent = (planId: string) => currentPlanId === planId;

  // ── Export helpers ────────────────────────────────────────────────
  // Both lazy-load their respective lib so the ~800kB combined cost of
  // xlsx + jspdf only hits the bundle when an admin actually clicks
  // export. First click ≈ 500ms cold-load; subsequent clicks are
  // instant (modules cached by the import map).
  //
  // Row shape is identical across both exporters: one row per "stat or
  // feature", one column per plan. Stat rows are pre-formatted strings
  // (e.g. "Unlimited", "5 GB"); feature rows render "Yes" / "No" plus
  // the limit value when the feature has a numeric cap. Categories
  // appear as section header rows so the export reads like the screen.
  const buildExportRows = (): { header: string[]; rows: (string | number)[][] } => {
    const header = ['Section', 'Feature', ...plans.map(p => p.display_name)];
    const rows: (string | number)[][] = [];

    // Plan price + stats (mirrors the on-screen header block)
    rows.push([
      'Plan Stats',
      'Price (per seat / mo)',
      ...plans.map(p => p.name === 'enterprise' ? 'Custom' : formatPrice(p.price_per_seat_monthly)),
    ]);
    STAT_ROWS.forEach(stat => {
      rows.push(['Plan Stats', stat.label, ...plans.map(p => stat.getValue(p))]);
    });

    // App / B2C features
    appCategories.forEach(([cat, keys]) => {
      keys.forEach(key => {
        const meta = FEATURE_META[key];
        rows.push([
          `B2C · ${cat}`,
          meta.label,
          ...plans.map(p => {
            const feat = featureMap[p.id]?.[key];
            if (feat?.enabled !== true) return 'No';
            if (meta.hasLimit) return formatLimit(feat.limit);
            return 'Yes';
          }),
        ]);
      });
    });

    // Platform / B2B features
    platformCategories.forEach(([cat, keys]) => {
      keys.forEach(key => {
        const meta = FEATURE_META[key];
        rows.push([
          `B2B · ${cat}`,
          meta.label,
          ...plans.map(p => {
            const feat = featureMap[p.id]?.[key];
            if (feat?.enabled !== true) return 'No';
            if (meta.hasLimit) return formatLimit(feat.limit);
            return 'Yes';
          }),
        ]);
      });
    });

    return { header, rows };
  };

  // `exporting` state is declared at the top of the component with the
  // other hooks (above the loading/error/empty early returns). This
  // is the click-handler section.

  const handleExportExcel = async () => {
    if (exporting) return;
    setExporting('xlsx');
    try {
      // Dynamic import so xlsx (~430 kB) stays out of the initial
      // bundle — only loads when an admin actually clicks Export.
      const XLSX = await import('xlsx');
      const { header, rows } = buildExportRows();
      const aoa = [header, ...rows];
      const ws  = XLSX.utils.aoa_to_sheet(aoa);

      // Sensible column widths: section + feature stay narrow,
      // plan columns sized to fit the longest cell.
      const colWidths = aoa[0].map((_, colIdx) => {
        const maxLen = Math.max(...aoa.map(r => String(r[colIdx] ?? '').length));
        return { wch: Math.min(Math.max(maxLen + 2, 12), 40) };
      });
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Platform Plans');
      XLSX.writeFile(wb, `platform-plans-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) {
      console.error('[PlatformPlans] Excel export failed:', e);
      alert('Excel export failed — see console.');
    } finally {
      setExporting(null);
    }
  };

  const handleExportPdf = async () => {
    if (exporting) return;
    setExporting('pdf');
    try {
      // Same lazy-load pattern as Excel — jsPDF + autotable land
      // ~390kB combined and are only needed at export time.
      const { default: jsPDF } = await import('jspdf');
      const autoTableMod       = await import('jspdf-autotable');
      const autoTable          = (autoTableMod as any).default ?? autoTableMod;

      // Landscape A3 — the 4-plan matrix barely fits on portrait A4.
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' });

      doc.setFontSize(16);
      doc.text('Platform Plans', 40, 40);
      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text(`Generated ${new Date().toLocaleString()}`, 40, 58);

      const { header, rows } = buildExportRows();
      autoTable(doc, {
        startY:    78,
        head:      [header],
        body:      rows,
        styles:    { fontSize: 9, cellPadding: 4 },
        headStyles:{ fillColor: [41, 73, 122], textColor: 255, fontStyle: 'bold' },
        // Section column slightly dimmer so the eye lands on Feature + plan cells
        columnStyles: { 0: { fillColor: [245, 247, 250], textColor: 80, fontStyle: 'bold' } },
        // Repeat the section name only when it changes — easier to read
        didParseCell: (data: any) => {
          if (data.column.index === 0 && data.row.index > 0) {
            const prev = rows[data.row.index - 1]?.[0];
            if (prev === data.cell.raw) data.cell.text = [''];
          }
        },
      });

      doc.save(`platform-plans-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      console.error('[PlatformPlans] PDF export failed:', e);
      alert('PDF export failed — see console.');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-surface-900">Platform Plans</h1>
          <p className="text-sm text-surface-500 mt-1">
            Compare all available B2B platform plans side by side.
          </p>
        </div>
        {/* Export controls — Excel and PDF. Disabled while a previous
            export is mid-flight so a double-click doesn't queue two
            downloads. Lazy-loaded libs mean the first click takes a
            beat; status text inside the button signals the wait. */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportExcel}
            disabled={!!exporting}
            className={cn(
              'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors',
              'border-overlay/[0.12] text-surface-800 hover:bg-overlay/[0.04]',
              exporting && 'opacity-60 cursor-not-allowed',
            )}
          >
            <FileSpreadsheet size={15} />
            {exporting === 'xlsx' ? 'Exporting…' : 'Export to Excel'}
          </button>
          <button
            onClick={handleExportPdf}
            disabled={!!exporting}
            className={cn(
              'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors',
              'border-overlay/[0.12] text-surface-800 hover:bg-overlay/[0.04]',
              exporting && 'opacity-60 cursor-not-allowed',
            )}
          >
            <FileText size={15} />
            {exporting === 'pdf' ? 'Exporting…' : 'Export to PDF'}
          </button>
        </div>
      </div>

      <div className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            {/* ── Plan Headers ──────────────────────────────────────── */}
            <thead>
              <tr className="border-b border-overlay/[0.06]">
                <th className="sticky left-0 z-10 bg-surface-0 dark:bg-surface-100 after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-overlay/[0.08] p-4 text-left w-[200px] min-w-[200px]" />
                {plans.map((plan) => (
                  <th
                    key={plan.id}
                    className={cn(
                      'p-4 text-center align-top min-w-[160px]',
                      isCurrent(plan.id) && 'bg-brand-500/[0.04]',
                    )}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: plan.color || '#64748b' }}
                      />
                      <div className="text-sm font-semibold text-surface-900">{plan.display_name}</div>
                      {plan.description && (
                        <p className="text-[11px] text-surface-500 line-clamp-2 max-w-[140px]">{plan.description}</p>
                      )}
                      <div className="text-lg font-bold text-surface-900">
                        {plan.name === 'enterprise' ? 'Custom' : formatPrice(plan.price_per_seat_monthly)}
                        {plan.name !== 'enterprise' && plan.price_per_seat_monthly > 0 && (
                          <span className="text-xs font-normal text-surface-500">/seat/mo</span>
                        )}
                      </div>
                      {isCurrent(plan.id) && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                          Current Plan
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* ── Plan Stats ──────────────────────────────────────── */}
              {STAT_ROWS.map((stat) => {
                const Icon = stat.icon;
                return (
                  <tr key={stat.label} className="border-b border-overlay/[0.04]">
                    <td className="sticky left-0 z-10 bg-surface-0 dark:bg-surface-100 after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-overlay/[0.08] px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <Icon size={14} className="text-surface-400 shrink-0" />
                        <span className="text-sm text-surface-700 font-medium">{stat.label}</span>
                      </div>
                    </td>
                    {plans.map((plan) => (
                      <td
                        key={plan.id}
                        className={cn(
                          'px-4 py-2.5 text-center text-sm font-semibold text-surface-800 tabular-nums',
                          isCurrent(plan.id) && 'bg-brand-500/[0.04]',
                        )}
                      >
                        {stat.getValue(plan)}
                      </td>
                    ))}
                  </tr>
                );
              })}

              {/* ── App Features ─────────────────────────────────────── */}
              {appCategories.length > 0 && (
                <>
                  <tr>
                    <td className="sticky left-0 z-10 bg-overlay/[0.03] px-4 py-2.5 border-y border-overlay/[0.06]">
                      <span className="text-[10px] font-bold text-surface-600 uppercase tracking-wider">
                        App Features
                      </span>
                    </td>
                    {plans.map((p) => (
                      <td key={p.id} className="bg-overlay/[0.03] border-y border-overlay/[0.06]" />
                    ))}
                  </tr>

                  {appCategories.map(([cat, keys]) => {
                    const collapseKey = `app:${cat}`;
                    const isCollapsed = collapsed.has(collapseKey);
                    return (
                    <>
                      <tr key={`cat-${cat}`}>
                        <td
                          className="sticky left-0 z-10 bg-overlay/[0.015] px-4 py-1.5 border-b border-overlay/[0.04] cursor-pointer hover:bg-overlay/[0.04] transition-colors"
                          onClick={() => toggleCat(collapseKey)}
                        >
                          <div className="flex items-center gap-1.5">
                            <ChevronDown
                              size={11}
                              className={`text-surface-500 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                            />
                            <span className="text-[10px] font-semibold text-brand-600 dark:text-brand-400 uppercase tracking-wider">
                              {cat}
                            </span>
                            <span className="ml-2 text-[10px] text-surface-500 font-normal normal-case tracking-normal">
                              ({keys.length})
                            </span>
                          </div>
                        </td>
                        {plans.map((p) => (
                          <td key={p.id} className="bg-overlay/[0.015] border-b border-overlay/[0.04]" />
                        ))}
                      </tr>
                      {!isCollapsed && keys.map((key) => {
                        const meta = FEATURE_META[key];
                        return (
                          <tr key={key} className="border-b border-overlay/[0.04]">
                            <td className="sticky left-0 z-10 bg-surface-0 dark:bg-surface-100 after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-overlay/[0.08] px-4 py-2.5">
                              <div className="text-sm text-surface-800 font-medium">{meta.label}</div>
                              <div className="text-[11px] text-surface-500 mt-0.5">{meta.description}</div>
                            </td>
                            {plans.map((plan) => {
                              const feat = featureMap[plan.id]?.[key];
                              const enabled = feat?.enabled === true;
                              return (
                                <td
                                  key={plan.id}
                                  className={cn(
                                    'px-4 py-2.5 text-center',
                                    isCurrent(plan.id) && 'bg-brand-500/[0.04]',
                                    !enabled && 'opacity-40',
                                  )}
                                >
                                  {enabled ? (
                                    <div className="flex flex-col items-center gap-1">
                                      <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto">
                                        <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                      </div>
                                      {meta.hasLimit && (
                                        <span className="text-[11px] font-semibold text-brand-600 dark:text-brand-400 tabular-nums">
                                          {feat?.limit !== null && feat?.limit !== undefined
                                            ? formatLimit(feat.limit)
                                            : <Infinity className="w-3.5 h-3.5 inline" />}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="w-5 h-5 rounded-full bg-surface-200/30 flex items-center justify-center mx-auto">
                                      <Lock className="w-3 h-3 text-surface-500" />
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </>
                    );
                  })}
                </>
              )}

              {/* ── Platform Features ────────────────────────────────── */}
              {platformCategories.length > 0 && (
                <>
                  <tr>
                    <td className="sticky left-0 z-10 bg-overlay/[0.03] px-4 py-2.5 border-y border-overlay/[0.06]">
                      <span className="text-[10px] font-bold text-surface-600 uppercase tracking-wider">
                        Platform Features
                      </span>
                    </td>
                    {plans.map((p) => (
                      <td key={p.id} className="bg-overlay/[0.03] border-y border-overlay/[0.06]" />
                    ))}
                  </tr>

                  {platformCategories.map(([cat, keys]) => {
                    const collapseKey = `platform:${cat}`;
                    const isCollapsed = collapsed.has(collapseKey);
                    return (
                    <>
                      <tr key={`pcat-${cat}`}>
                        <td
                          className="sticky left-0 z-10 bg-overlay/[0.015] px-4 py-1.5 border-b border-overlay/[0.04] cursor-pointer hover:bg-overlay/[0.04] transition-colors"
                          onClick={() => toggleCat(collapseKey)}
                        >
                          <div className="flex items-center gap-1.5">
                            <ChevronDown
                              size={11}
                              className={`text-surface-500 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                            />
                            <span className="text-[10px] font-semibold text-brand-600 dark:text-brand-400 uppercase tracking-wider">
                              {cat}
                            </span>
                            <span className="ml-2 text-[10px] text-surface-500 font-normal normal-case tracking-normal">
                              ({keys.length})
                            </span>
                          </div>
                        </td>
                        {plans.map((p) => (
                          <td key={p.id} className="bg-overlay/[0.015] border-b border-overlay/[0.04]" />
                        ))}
                      </tr>
                      {!isCollapsed && keys.map((key) => {
                        const meta = FEATURE_META[key];
                        return (
                          <tr key={key} className="border-b border-overlay/[0.04]">
                            <td className="sticky left-0 z-10 bg-surface-0 dark:bg-surface-100 after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-overlay/[0.08] px-4 py-2.5">
                              <div className="text-sm text-surface-800 font-medium">{meta.label}</div>
                              <div className="text-[11px] text-surface-500 mt-0.5">{meta.description}</div>
                            </td>
                            {plans.map((plan) => {
                              const feat = featureMap[plan.id]?.[key];
                              const enabled = feat?.enabled === true;
                              return (
                                <td
                                  key={plan.id}
                                  className={cn(
                                    'px-4 py-2.5 text-center',
                                    isCurrent(plan.id) && 'bg-brand-500/[0.04]',
                                    !enabled && 'opacity-40',
                                  )}
                                >
                                  {enabled ? (
                                    <div className="flex flex-col items-center gap-1">
                                      <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto">
                                        <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                      </div>
                                      {meta.hasLimit && (
                                        <span className="text-[11px] font-semibold text-brand-600 dark:text-brand-400 tabular-nums">
                                          {feat?.limit !== null && feat?.limit !== undefined
                                            ? formatLimit(feat.limit)
                                            : <Infinity className="w-3.5 h-3.5 inline" />}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="w-5 h-5 rounded-full bg-surface-200/30 flex items-center justify-center mx-auto">
                                      <Lock className="w-3 h-3 text-surface-500" />
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
